import { useEffect, useMemo, useState } from "react";
import Box from "elements/Box";
import { Button, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import TuneIcon from '@mui/icons-material/Tune';

import "assets/css/main.css";

import AssetsEditor from "./AssetsEditor";
import { ApiRequest } from "GlobalFunctions";
import Filters2 from 'elements/Filters/Filters2';
import FiltersApplied2 from 'elements/Filters/FiltersApplied2';

export default function AssetsList(props: any) {
  const { setLoading } = props;

  useEffect(() => {
    setLoading(false);
  }, []);

  // Define filters first so we can use labels for normalization
  const assetsFiltersDef = [
    { type: "autocomplete", label: "Cliente", name: "client_id", value: "", endpoint: "/clients/list" },
    { type: "text", label: "Descripción del activo", name: "description", value: "" },
    { type: "text", label: "Serial", name: "serial", value: "" },
    { type: "autocomplete", label: "Zona", name: "zone", value: "", endpoint: "/zones/list" },
    { type: "autocomplete", label: "Asesor", name: "advisor_id", value: "", endpoint: "/users/listByRole", endpoint_params: { role_ids: [4,6,7] } },
  ];

  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [openEditor, setOpenEditor] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);

  // Filters (apply-on-click using shared Filters2 UI)
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersDialogKey, setFiltersDialogKey] = useState(0);

  const buildInitialFilters = () => {
    try {
      const savedRaw = localStorage.getItem("assetsFilters");
      const saved = savedRaw ? JSON.parse(savedRaw) : {};
      if (!saved || typeof saved !== 'object') return {};
      const defMap = new Map(assetsFiltersDef.map((f: any) => [f.name, f]));
      const normalized: any = {};
      Object.entries(saved).forEach(([name, v]: any) => {
        const def: any = defMap.get(name);
        if (!def) return; // unknown key
        if (v && typeof v === 'object' && (v.value !== undefined || v.value_txt !== undefined || v.label !== undefined)) {
          if (v.value !== null && v.value !== '' && !(Array.isArray(v.value) && v.value.length === 0)) {
            normalized[name] = v;
          }
          return;
        }
        if (v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
          normalized[name] = { value: v, value_txt: typeof v === 'string' ? v : undefined, label: def.label };
        }
      });
      return normalized;
    } catch {
      return {};
    }
  };

  const [filtersValues, setFiltersValues] = useState<any>(buildInitialFilters);

  const sanitizeFilters = (raw: any) => {
    const cleaned: any = {};
    if (!raw || typeof raw !== 'object') return cleaned;
    Object.entries(raw).forEach(([k, v]: any) => {
      if (!v || typeof v !== 'object') return;
      const val = v.value;
      if (val === null || val === '' || (Array.isArray(val) && val.length === 0)) return;
      cleaned[k] = v;
    });
    return cleaned;
  };

  const displayedFilters = useMemo(() => sanitizeFilters(filtersValues), [filtersValues]);

  useEffect(() => {
    setLoading(true);
    loadList();
  }, [page, displayedFilters]);

  const loadList = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/purchases/assets/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        page,
        filters: displayedFilters,
      },
      setResponse: (response: any) => {
        const items = response.list || [];
        setList(items);
        setTotalPages(response.total_pages || 0);
        setLoading(false);
      },
    });

    // localStorage.setItem("assetsPage", page.toString());
  };

  const setFilters = (tempFilters: any) => {
    const clean = sanitizeFilters(tempFilters);
    setFiltersValues(clean);
    localStorage.setItem('assetsFilters', JSON.stringify(clean));
    setPage(1);
  };

  const renderClientCell = (item: any) => {
    const name = item.business_name || item.client_name || "-";
    const code = item.client_code || item.document || "";
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span>{name}</span>
        {code ? <span style={{ color: "#777", fontSize: 12 }}>{code}</span> : null}
      </div>
    );
  };

  return (
    <div id="AssetsList" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Activos</h2>
            <span className="h2_subtitle">Listado de activos</span>
          </div>
          <div className="boxContainerActions" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Button variant="outlined" color="primary" startIcon={<TuneIcon />} onClick={() => { setFiltersDialogKey((k) => k + 1); setOpenFilters(true); }}>
              Filtros
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setActiveItem(null);
                setOpenEditor(true);
              }}
              startIcon={<AddIcon />}
            >
              Nuevo activo
            </Button>
          </div>
        </div>

        <Filters2
          key={filtersDialogKey}
          open={openFilters}
          setOpen={setOpenFilters}
          filters={assetsFiltersDef}
          setFilters={setFilters}
        />
        <FiltersApplied2 filtersValues={displayedFilters} setFiltersValues={(v: any) => {
          // when chip removes, also persist cleaned filters
          const clean = sanitizeFilters(v);
          setFiltersValues(clean);
          localStorage.setItem('assetsFilters', JSON.stringify(clean));
        }} filters={assetsFiltersDef} />

        <Box id="AssetsListIn" className="box100">
          <TableContainer>
            <Table aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>ID</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Cliente</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Descripción</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Serial</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Contrato</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Tamaño</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Fecha</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Comentarios</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((item, i) => (
                  <TableRow key={i} hover role="checkbox">
                    <TableCell>{item.asset_id}</TableCell>
                    <TableCell>{renderClientCell(item)}</TableCell>
                    <TableCell>{item.description || '-'}</TableCell>
                    <TableCell>{item.serial || '-'}</TableCell>
                    <TableCell>{item.contract || '-'}</TableCell>
                    <TableCell>{item.size || '-'}</TableCell>
                    <TableCell>{item.created_at || '-'}</TableCell>
                    <TableCell>{item.comments || '-'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        style={{ marginRight: "10px" }}
                        startIcon={<ModeEditIcon />}
                        onClick={() => {
                          setActiveItem(item.asset_id);
                          setOpenEditor(true);
                        }}
                      >
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            style={{ marginTop: "20px", alignSelf: "center" }}
          />
        </Box>
      </div>

      <AssetsEditor
        setLoading={setLoading}
        openEditor={openEditor}
        setOpenEditor={setOpenEditor}
        asset_id={activeItem}
        loadList={loadList}
      />
    </div>
  );
}
